// Palette rules for ctx.charts — the part of the data-visualization standard
// that can be enforced by code rather than documented and hoped for.
// KEEP IN SYNC with ./charts-palette.d.mts.
//
// Three rules live here, and each one is a function that refuses rather than a
// paragraph that advises:
//   1. Categorical hues are a FIXED ordered set of 8. There is no 9th hue and
//      no cycling — `assignSlots` either throws or folds the overflow into an
//      explicit "Other" bucket.
//   2. Colour follows the ENTITY, not its rank. `assignSlots` takes the previous
//      assignment and preserves it, so hiding series 2 does not repaint 3..8.
//   3. Status colours are a separate namespace. They are never reachable from
//      the categorical ramp, and `statusColor` will not return one without a
//      label to pair it with — colour alone is not a status signal.

import { hexToHsl, hslToHex, mix, modeForSurface } from "./charts-color.mjs";

/**
 * Validated against `--os-window-body` #0e1017 — all six checks of the palette
 * validator pass. Slot order is part of the contract: do not re-order, do not
 * re-pick. A chart that needs a different hue needs a different *entity*.
 */
export const CATEGORICAL_DARK = Object.freeze([
  "#3987e5",
  "#d95926",
  "#199e70",
  "#c98500",
  "#d55181",
  "#008300",
  "#9085e9",
  "#e66767",
]);

/**
 * Validated against the windows-classic surface #d4d0c8. All checks pass but
 * with a CONTRAST WARN: 6 of 8 hues fall below 3:1 against that surface.
 *
 * Per the standard that warning is NOT dismissable — it obligates visible
 * labels or a table view. `lightModeReliefRequired()` reports that obligation
 * and the chart host acts on it (legend + direct labels + table affordance are
 * forced in light mode). Do not "fix" this by darkening the hues; they were
 * chosen by the validator against this exact surface.
 */
export const CATEGORICAL_LIGHT = Object.freeze([
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#4a3aa7",
  "#e34948",
]);

/** There is no slot 9. This number is the whole point of the rule. */
export const MAX_CATEGORICAL_SLOTS = 8;

/** Neutral grey for the explicit "Other" bucket — deliberately not a hue. */
export const OTHER_COLOR = Object.freeze({ dark: "#8b919c", light: "#6f6d68" });

/** Neutral midpoint for diverging ramps. Never a hue; that is the rule. */
export const DIVERGING_MIDPOINT = Object.freeze({ dark: "#6e727a", light: "#b9b6b0" });

/**
 * Reserved status colours. NEVER available as "series 4" — they are a distinct
 * namespace with a distinct meaning, and reusing them destroys that meaning.
 */
export const STATUS_COLORS = Object.freeze({
  dark: Object.freeze({
    good: "#30d158",
    warning: "#ffd60a",
    serious: "#ff9f0a",
    critical: "#ff453a",
  }),
  light: Object.freeze({
    good: "#1c7f3d",
    warning: "#8a6d00",
    serious: "#b4560a",
    critical: "#c02a20",
  }),
});

export const STATUS_LEVELS = Object.freeze(["good", "warning", "serious", "critical"]);

/** @param {unknown} mode @returns {"light"|"dark"} */
function normMode(mode) {
  return mode === "light" ? "light" : "dark";
}

/** The fixed categorical ramp for a mode. Returns a copy; the source is frozen. */
export function categoricalPalette(mode) {
  return (normMode(mode) === "light" ? CATEGORICAL_LIGHT : CATEGORICAL_DARK).slice();
}

/**
 * Colour for a categorical slot. Slots are 0..7 and nothing else: an
 * out-of-range slot throws instead of wrapping, because wrapping is exactly the
 * bug this rule exists to prevent (two entities, one hue, silently).
 * @param {number} slot
 * @param {"light"|"dark"} mode
 */
export function categoricalColor(slot, mode) {
  if (!Number.isInteger(slot) || slot < 0 || slot >= MAX_CATEGORICAL_SLOTS) {
    throw new RangeError(
      `charts: categorical slot ${slot} is out of range (0..${MAX_CATEGORICAL_SLOTS - 1}). ` +
        `The palette is not cyclic — group the long tail into an "Other" bucket instead.`
    );
  }
  return categoricalPalette(mode)[slot];
}

/**
 * A reserved status colour. `label` is required and must be non-empty: a status
 * must always be carried by an icon or a word, never by colour alone (colour
 * alone fails for ~8% of viewers and for every greyscale print).
 * @param {"good"|"warning"|"serious"|"critical"} level
 * @param {{ label?: string, mode?: "light"|"dark" }} opts
 */
export function statusColor(level, opts) {
  const o = opts || {};
  if (!STATUS_LEVELS.includes(level)) {
    throw new RangeError(
      `charts: unknown status level "${level}" (expected ${STATUS_LEVELS.join(" | ")})`
    );
  }
  if (typeof o.label !== "string" || !o.label.trim()) {
    throw new Error(
      `charts: statusColor("${level}") requires a { label } — status may never be ` +
        `communicated by colour alone. Pass the word or icon that accompanies it.`
    );
  }
  return STATUS_COLORS[normMode(o.mode)][level];
}

/**
 * Assign categorical slots to entity keys.
 *
 * `previous` is what makes colour follow the entity: keys already carrying a
 * slot keep it, so filtering a chart down to three series leaves those three
 * exactly the colour they had. New keys take the lowest FREE slot in the order
 * they are given. `pinned` outranks both: the caller names the slot outright,
 * ahead of any prior assignment or first-seen order.
 *
 * @param {readonly string[]} keys entity keys, in first-seen order
 * @param {{ previous?: Record<string, number>, pinned?: Record<string, number>, overflow?: "error"|"other", max?: number }} [opts]
 * @returns {{ slots: Record<string, number>, other: string[], overflowed: boolean }}
 */
export function assignSlots(keys, opts) {
  const o = opts || {};
  const max = Number.isInteger(o.max) ? o.max : MAX_CATEGORICAL_SLOTS;
  const overflow = o.overflow === "other" ? "other" : "error";
  const list = [];
  const seen = new Set();
  for (const k of keys || []) {
    const key = String(k);
    if (seen.has(key)) continue; // duplicate keys are one entity, not two
    seen.add(key);
    list.push(key);
  }

  /** @type {Record<string, number>} */
  const slots = {};
  const taken = new Set();

  // Pass 0 — explicit pins. The caller has named the slot, which outranks both
  // a prior assignment and first-seen order. An out-of-range or already-taken
  // pin is DROPPED rather than thrown: a pin is a preference about hue, and
  // failing a whole chart over one is worse than the colour being wrong.
  const pinned = o.pinned || {};
  for (const key of list) {
    const s = pinned[key];
    if (Number.isInteger(s) && s >= 0 && s < max && !taken.has(s)) {
      slots[key] = s;
      taken.add(s);
    }
  }

  // Pass 1 — honour prior assignments for surviving keys.
  const prev = o.previous || {};
  for (const key of list) {
    if (key in slots) continue; // pinned already
    const s = prev[key];
    if (Number.isInteger(s) && s >= 0 && s < max && !taken.has(s)) {
      slots[key] = s;
      taken.add(s);
    }
  }

  // Pass 2 — new keys take the lowest free slot.
  /** @type {string[]} */
  const other = [];
  let cursor = 0;
  for (const key of list) {
    if (key in slots) continue;
    while (cursor < max && taken.has(cursor)) cursor++;
    if (cursor >= max) {
      if (overflow === "error") {
        throw new RangeError(
          `charts: series "${key}" would need categorical slot ${max}, but the palette ` +
            `has exactly ${max} hues and is never cycled. Either reduce to ${max} series ` +
            `or pass { overflow: "other" } to fold the tail into a neutral "Other" bucket.`
        );
      }
      other.push(key);
      continue;
    }
    slots[key] = cursor;
    taken.add(cursor);
    cursor++;
  }

  return { slots, other, overflowed: other.length > 0 };
}

/**
 * `assignSlots` + hue lookup in one step. Keys in the "Other" bucket all share
 * the neutral grey — that is the honest rendering of "these are not
 * individually identified".
 * @returns {{ colors: Record<string,string>, slots: Record<string,number>, other: string[] }}
 */
export function resolveSeriesColors(keys, opts) {
  const o = opts || {};
  const mode = normMode(o.mode);
  const { slots, other } = assignSlots(keys, o);
  /** @type {Record<string,string>} */
  const colors = {};
  for (const [key, slot] of Object.entries(slots)) colors[key] = categoricalColor(slot, mode);
  for (const key of other) colors[key] = OTHER_COLOR[mode];
  return { colors, slots, other };
}

/**
 * Sequential ramp: ONE hue, light → dark. Not a rainbow — the hue and
 * saturation of `base` are held fixed and only lightness moves, which is what
 * makes the ordering readable without a legend.
 * @param {number} steps
 * @param {{ base?: string, mode?: "light"|"dark" }} [opts]
 * @returns {string[]}
 */
export function sequentialRamp(steps, opts) {
  const o = opts || {};
  const n = Math.max(2, Math.floor(steps));
  const mode = normMode(o.mode);
  const base = o.base || categoricalColor(0, mode);
  const { h, s } = hexToHsl(base);
  // Endpoints differ per mode so both ends stay distinguishable from the
  // surface: on dark, the "light" end stops short of white.
  const lo = mode === "light" ? 0.92 : 0.82;
  const hi = mode === "light" ? 0.26 : 0.2;
  const out = [];
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0 : i / (n - 1);
    out.push(hslToHex({ h, s: Math.max(0.18, s * (0.55 + 0.45 * t)), l: lo + (hi - lo) * t }));
  }
  return out;
}

/**
 * Diverging ramp: TWO hues with a NEUTRAL GREY midpoint.
 *
 * `steps` must be odd. That is not fussiness — an even ramp has no midpoint
 * bucket, so the zero crossing lands inside a coloured band and the reader
 * cannot see where it is. Refusing an even count is cheaper than explaining the
 * resulting chart.
 * @param {number} steps odd, >= 3
 * @param {{ low?: string, high?: string, mode?: "light"|"dark" }} [opts]
 * @returns {string[]}
 */
export function divergingRamp(steps, opts) {
  const o = opts || {};
  const n = Math.floor(steps);
  if (!Number.isInteger(n) || n < 3 || n % 2 === 0) {
    throw new RangeError(
      `charts: divergingRamp needs an ODD step count >= 3 (got ${steps}). An even ramp ` +
        `has no midpoint bucket, so the neutral zero crossing cannot be shown.`
    );
  }
  const mode = normMode(o.mode);
  const pal = categoricalPalette(mode);
  const low = o.low || pal[0]; // blue by default
  const high = o.high || pal[1]; // orange by default
  const midpoint = DIVERGING_MIDPOINT[mode];
  const half = (n - 1) / 2;
  const out = [];
  for (let i = 0; i < half; i++) out.push(mix(low, midpoint, i / half));
  out.push(midpoint);
  for (let i = 1; i <= half; i++) out.push(mix(midpoint, high, i / half));
  return out;
}

/**
 * Does this surface oblige visible-label relief?
 *
 * True for every light surface. The light palette carries a non-dismissable
 * contrast WARN (6 of 8 hues below 3:1 against #d4d0c8), and the standard's
 * remedy for that is labels or a table — not a different palette. The chart
 * host reads this and forces legend + direct labels + the table affordance, so
 * an app author cannot switch to a light theme and quietly lose the relief.
 */
export function lightModeReliefRequired(surfaceOrMode) {
  const mode =
    surfaceOrMode === "light" || surfaceOrMode === "dark"
      ? surfaceOrMode
      : modeForSurface(surfaceOrMode);
  return mode === "light";
}
